import { useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Eye, SearchX } from "lucide-react";
import { Temporal } from "temporal-polyfill";
import { useTRPC } from "@/trpc";
import { Button } from "@/components/ui/button";
import { useOrganization } from "@/hooks/use-organization";
import { useOrgPath } from "@/hooks/useOrgPath";
import {
  RecordLoadFailed,
  RecordNotInBusinessUnit,
} from "@/components/page-message";
import {
  isRecordUnavailableError,
  retryUnlessRecordUnavailable,
} from "@/common/utils/record-unavailable-error";
import { useRolePermissions, Permission } from "@/hooks/use-role-permissions";
import { CandidateCard } from "./job-dashboard/candidate-card";
import { CandidateDetailSheet } from "./job-dashboard/candidate-detail-sheet";
import { CandidateListToolbar } from "./job-dashboard/candidate-list-toolbar";
import { CandidatePagination } from "./job-dashboard/candidate-pagination";
import { CandidateViewNav } from "./job-dashboard/candidate-view-nav";
import {
  CandidateSort,
  CandidateViewKind,
  EMPTY_CANDIDATE_FILTERS,
  countCandidatesByView,
  countInProcessCandidates,
  countNewCandidates,
  filterCandidates,
  filterCandidatesByView,
  getCandidateFilterOptions,
  getViewLabel,
  paginate,
  searchCandidates,
  sortCandidates,
  type CandidateFilters,
  type CandidatePermissions,
  type CandidateViewKey,
  type LapsedRequirement,
} from "./job-dashboard/candidate-views";
import { JobDashboardDialogs } from "./job-dashboard/job-dashboard-dialogs";
import { JobDashboardHeader } from "./job-dashboard/job-dashboard-header";
import { JobDashboardStats } from "./job-dashboard/job-dashboard-stats";
import { JobDetailsDisclosure } from "./job-dashboard/job-details-disclosure";
import { useJobDashboardActions } from "./job-dashboard/use-job-dashboard-actions";

const NO_LAPSED_REQUIREMENTS: LapsedRequirement[] = [];

function getEmptyViewCopy({
  view,
  label,
}: {
  view: CandidateViewKey;
  label: string;
}): { title: string; description: string } {
  switch (view) {
    case CandidateViewKind.All:
      return {
        title: "No candidates yet",
        description:
          "Candidates who express interest in this position will appear here.",
      };
    case CandidateViewKind.Hired:
      return {
        title: "No hired candidates",
        description: "Candidates you mark as hired will appear here.",
      };
    case CandidateViewKind.Rejected:
      return {
        title: "No rejected candidates",
        description: "Candidates you reject will appear here.",
      };
    case CandidateViewKind.Withdrawn:
      return {
        title: "No withdrawn candidates",
        description:
          "Candidates who withdraw their interest in the last 30 days will appear here.",
      };
  }
  return {
    title: `No candidates in ${label}`,
    description: "Candidates you move into this stage will appear here.",
  };
}

function EmptyState({
  title,
  description,
  icon: Icon,
  action,
}: {
  title: string;
  description: string;
  icon: typeof Eye;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-12 text-center">
      <Icon
        aria-hidden="true"
        className="mx-auto mb-4 h-12 w-12 text-slate-300"
      />
      <h3 className="mb-1.5 text-base font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500">{description}</p>
      {action && (
        <Button variant="outline" className="mt-4" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading job dashboard"
      className="flex min-h-80 items-center justify-center"
    >
      <div className="animate-pulse flex flex-col items-center">
        <div className="h-8 w-32 bg-slate-200 rounded-md mb-4"></div>
        <div className="h-4 w-48 bg-slate-200 rounded-md"></div>
      </div>
    </div>
  );
}

export function JobDashboard({
  jobId,
  user,
}: {
  jobId: string;
  user: { id?: string; is_admin: boolean } | null | undefined;
}) {
  const trpc = useTRPC();
  const orgPath = useOrgPath();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { hasPermission } = useRolePermissions(user);
  const [view, setView] = useState<CandidateViewKey>(CandidateViewKind.All);
  const [filters, setFilters] = useState<CandidateFilters>(
    EMPTY_CANDIDATE_FILTERS,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [sort, setSort] = useState<CandidateSort>(CandidateSort.NewestApplied);
  const [page, setPage] = useState(1);
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [now] = useState(() => Temporal.Now.instant());

  const permissions: CandidatePermissions = {
    canManageJobPosts: hasPermission(Permission.ManageJobPosts),
    canManageAffiliations: hasPermission(Permission.ManageAffiliations),
    canManageProviderPools: hasPermission(Permission.ManageProviderPools),
    canRevealProviders: hasPermission(Permission.ViewJobOffers),
  };

  const {
    data: job,
    isLoading: jobLoading,
    error: jobError,
    refetch: refetchJob,
  } = useQuery(
    trpc.jobPost.orgGet.queryOptions(
      {
        id: jobId,
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      },
      { retry: retryUnlessRecordUnavailable },
    ),
  );
  const { data: jobStats } = useQuery(
    trpc.jobPost.stats.queryOptions({
      id: jobId,
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    }),
  );
  const {
    data: candidates,
    error: candidatesError,
    refetch: refetchCandidates,
  } = useQuery({
    ...trpc.jobPost.getInterestedCandidates.queryOptions({
      id: jobId,
      includeRejected: true,
      includeWithdrawn: true,
    }),
    placeholderData: keepPreviousData,
    retry: retryUnlessRecordUnavailable,
  });

  const actions = useJobDashboardActions({
    jobId,
    jobTitle: job?.title ?? null,
  });

  const candidateUserIds = useMemo(
    () => (candidates ?? []).map((candidate) => candidate.userId),
    [candidates],
  );
  const { data: noteSummaryRows } = useQuery({
    ...trpc.jobPost.candidateNoteSummaries.queryOptions({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      userIds: candidateUserIds,
    }),
    enabled: permissions.canManageJobPosts && candidateUserIds.length > 0,
  });
  const { data: lapsedRequirementsData } = useQuery({
    ...trpc.jobPost.getLapsedRequirements.queryOptions({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      jobPostId: jobId,
      userIds: candidateUserIds.length > 0 ? candidateUserIds : undefined,
      includeResolved: false,
    }),
    enabled: permissions.canManageJobPosts && candidateUserIds.length > 0,
  });

  const noteSummaryByUser = useMemo(
    () => new Map((noteSummaryRows ?? []).map((row) => [row.userId, row])),
    [noteSummaryRows],
  );

  // Lapsed requirements grouped by user for O(1) lookup per card.
  const lapsedByUser = useMemo(() => {
    const grouped = new Map<string, LapsedRequirement[]>();
    for (const row of lapsedRequirementsData ?? []) {
      const existing = grouped.get(row.userId) ?? [];
      existing.push({
        id: row.id,
        displayLabel: row.displayLabel ?? "",
        severity: row.severity ?? "",
      });
      grouped.set(row.userId, existing);
    }
    return grouped;
  }, [lapsedRequirementsData]);

  const allCandidates = useMemo(() => candidates ?? [], [candidates]);
  const stages = useMemo(
    () =>
      selectedOrgBusinessUnit.enableHiringProcess
        ? (job?.hiringProcess?.stages ?? [])
        : [],
    [selectedOrgBusinessUnit.enableHiringProcess, job?.hiringProcess?.stages],
  );

  const counts = useMemo(
    () => countCandidatesByView({ candidates: allCandidates, stages }),
    [allCandidates, stages],
  );
  const newCount = useMemo(
    () =>
      countNewCandidates({
        candidates: filterCandidatesByView({
          candidates: allCandidates,
          view: CandidateViewKind.All,
        }),
        now,
      }),
    [allCandidates, now],
  );
  const inProcessCount = useMemo(
    () => countInProcessCandidates({ candidates: allCandidates, stages }),
    [allCandidates, stages],
  );
  const filterOptions = useMemo(
    () => getCandidateFilterOptions(allCandidates),
    [allCandidates],
  );

  const viewCandidates = useMemo(
    () => filterCandidatesByView({ candidates: allCandidates, view }),
    [allCandidates, view],
  );
  const visibleCandidates = useMemo(
    () =>
      sortCandidates({
        candidates: searchCandidates({
          candidates: filterCandidates({
            candidates: viewCandidates,
            filters,
          }),
          query: searchQuery,
        }),
        sort,
      }),
    [viewCandidates, filters, searchQuery, sort],
  );
  const { pageItems, pageCount, currentPage } = paginate({
    items: visibleCandidates,
    page,
  });

  const selectView = (nextView: CandidateViewKey) => {
    setView(nextView);
    setPage(1);
  };
  const changeFilters = (nextFilters: CandidateFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };
  const changeSearchQuery = (nextQuery: string) => {
    setSearchQuery(nextQuery);
    setPage(1);
  };
  const clearFiltersAndSearch = () => {
    setFilters(EMPTY_CANDIDATE_FILTERS);
    setSearchQuery("");
    setPage(1);
  };
  const changeSort = (nextSort: CandidateSort) => {
    setSort(nextSort);
    setPage(1);
  };

  if (jobLoading) {
    return <DashboardSkeleton />;
  }
  // Switching business units while viewing a job keeps the job's ID in the
  // URL, so the API answers NOT_FOUND for the newly selected business unit.
  if (
    isRecordUnavailableError(jobError) ||
    isRecordUnavailableError(candidatesError)
  ) {
    return (
      <RecordNotInBusinessUnit
        title="Job post not found"
        recordLabel="job post"
        back={{ to: orgPath("/jobs/post"), label: "Back to job posts" }}
      />
    );
  }
  if (jobError || candidatesError) {
    return (
      <RecordLoadFailed
        recordLabel="job post"
        onRetry={() => {
          void refetchJob();
          void refetchCandidates();
        }}
      />
    );
  }
  if (!job || !candidates) {
    return <DashboardSkeleton />;
  }

  const viewLabel = getViewLabel({ view, stages });
  const detailCandidate =
    allCandidates.find((candidate) => candidate.userId === detailUserId) ??
    null;
  const hasSearch = searchQuery.trim() !== "";
  const noMatchDescription = hasSearch
    ? `No ${viewLabel} candidates match "${searchQuery.trim()}".`
    : `No ${viewLabel} candidates match your filters.`;
  const emptyCopy = getEmptyViewCopy({ view, label: viewLabel });

  return (
    <div className="min-w-0 flex-1 bg-slate-50">
      <div className="mx-auto w-full min-w-0 max-w-7xl space-y-5 px-3 py-4 text-slate-900 sm:px-6 sm:py-6">
        <JobDashboardHeader
          job={job}
          jobId={jobId}
          userId={user?.id}
          canManageJobPosts={permissions.canManageJobPosts}
          actions={actions}
        />
        <JobDetailsDisclosure job={job} />
        <JobDashboardStats
          view={view}
          onSelectView={selectView}
          interestedCount={counts.all}
          newCount={newCount}
          inProcessCount={stages.length > 0 ? inProcessCount : null}
          hiredCount={counts.hired}
          notInterested={jobStats?.notInterestedReasons}
        />

        <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:gap-6">
          <CandidateViewNav
            view={view}
            onSelectView={selectView}
            counts={counts}
            stages={stages}
          />
          <section
            aria-label={`${viewLabel} candidates`}
            className="min-w-0 flex-1 space-y-4"
          >
            <CandidateListToolbar
              viewLabel={viewLabel}
              count={visibleCandidates.length}
              filters={filters}
              onFiltersChange={changeFilters}
              professionalTypes={filterOptions.professionalTypes}
              specialties={filterOptions.specialties}
              sort={sort}
              onSortChange={changeSort}
              searchQuery={searchQuery}
              onSearchQueryChange={changeSearchQuery}
            />
            {pageItems.length > 0 && (
              <div className="grid gap-4 lg:grid-cols-2">
                {pageItems.map((candidate) => (
                  <CandidateCard
                    key={candidate.id}
                    candidate={candidate}
                    stages={stages}
                    lapsed={
                      lapsedByUser.get(candidate.userId) ??
                      NO_LAPSED_REQUIREMENTS
                    }
                    permissions={permissions}
                    noteSummary={noteSummaryByUser.get(candidate.userId)}
                    actions={actions}
                    onOpenDetails={(selected) =>
                      setDetailUserId(selected.userId)
                    }
                  />
                ))}
              </div>
            )}
            {pageItems.length === 0 && viewCandidates.length > 0 && (
              <EmptyState
                icon={SearchX}
                title={
                  hasSearch
                    ? "No search results"
                    : "No matches for these filters"
                }
                description={noMatchDescription}
                action={{
                  label: hasSearch
                    ? "Clear search and filters"
                    : "Clear filters",
                  onClick: clearFiltersAndSearch,
                }}
              />
            )}
            {viewCandidates.length === 0 && (
              <EmptyState
                icon={Eye}
                title={emptyCopy.title}
                description={emptyCopy.description}
              />
            )}
            <CandidatePagination
              currentPage={currentPage}
              pageCount={pageCount}
              onPageChange={setPage}
            />
          </section>
        </div>

        <CandidateDetailSheet
          candidate={detailCandidate}
          jobId={jobId}
          lapsed={
            lapsedByUser.get(detailCandidate?.userId ?? "") ??
            NO_LAPSED_REQUIREMENTS
          }
          permissions={permissions}
          currentUser={
            user?.id ? { id: user.id, isAdmin: user.is_admin } : null
          }
          actions={actions}
          onClose={() => setDetailUserId(null)}
        />
        <JobDashboardDialogs jobId={jobId} user={user} actions={actions} />
      </div>
    </div>
  );
}
